import { useId } from 'react';

import {
  DiamondBaseColor,
  DiamondBasePosition,
} from '#src/components/series/SeriesBoard/HotStreakTile/Tile/utils.ts';

type Props = {
  color: DiamondBaseColor;
  position?: DiamondBasePosition;
  size?: number;
};
export const DiamondBase = ({
  color,
  position = DiamondBasePosition.up,
  size = 1,
}: Props) => {
  const orangeGradientId = useId();
  const greenGradientId = useId();
  const redGradientId = useId();
  const blueGradientId = useId();
  const grayGradientId = useId();

  const filterId = useId();

  const gradientId = {
    [DiamondBaseColor.orange]: orangeGradientId,
    [DiamondBaseColor.green]: greenGradientId,
    [DiamondBaseColor.red]: redGradientId,
    [DiamondBaseColor.blue]: blueGradientId,
    [DiamondBaseColor.gray]: grayGradientId,
  }[color];

  const gradients = (
    <>
      <radialGradient
        id={redGradientId}
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(46.5643 41.6939) scale(33.5638 50.4399)"
      >
        <stop stopColor="#D81616" />
        <stop offset="1" stopColor="#721717" />
      </radialGradient>
      <radialGradient
        id={orangeGradientId}
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(48.7701 41.6939) scale(35.8838 50.4399)"
      >
        <stop stopColor="#FEF314" />
        <stop offset="1" stopColor="#FF4E22" />
      </radialGradient>
      <radialGradient
        id={greenGradientId}
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(48.77 41.6939) scale(35.8838 50.4399)"
      >
        <stop stopColor="#00F34D" />
        <stop offset="1" stopColor="#00D5FF" />
      </radialGradient>
      <radialGradient
        id={blueGradientId}
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(48.7701 41.6939) scale(35.8838 50.4399)"
      >
        <stop stopColor="#7029FF" />
        <stop offset="1" stopColor="#1B034B" />
      </radialGradient>
      <radialGradient
        id={grayGradientId}
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(57.5903 46.3515) scale(39.4722 55.4839)"
      >
        <stop stopColor="#898989" />
        <stop offset="1" stopColor="#919191" />
      </radialGradient>
    </>
  );

  if (position === DiamondBasePosition.down) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: size * 100, height: size * 70 }}
        viewBox="0 0 100 70"
        fill="none"
      >
        <g filter={`url(#${filterId})`}>
          <path
            d="M49.6539 14.6538L14.6539 34.6538L49.6539 54.6538L84.6539 34.6538L49.6539 14.6538Z"
            fill={`url(#${gradientId})`}
          />
        </g>
        <defs>
          <filter
            id={filterId}
            x="9.41753e-06"
            y="-0.000112653"
            width="99.3078"
            height="69.3078"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feMorphology
              radius="1.48019"
              operator="dilate"
              in="SourceAlpha"
              result="effect1_dropShadow_10854_9250"
            />
            <feOffset />
            <feGaussianBlur stdDeviation="6.58686" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_10854_9250"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_10854_9250"
              result="shape"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="2.95127" />
            <feGaussianBlur stdDeviation="1.11015" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.75 0"
            />
            <feBlend
              mode="normal"
              in2="shape"
              result="effect2_innerShadow_10854_9250"
            />
          </filter>
          {gradients}
        </defs>
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: size * 100, height: size * 70 }}
      viewBox="0 0 100 70"
      fill="none"
    >
      <g filter={`url(#${filterId})`}>
        <path
          d="M49.6539 14.6538L14.6539 34.6538L49.6539 54.6538L84.6539 34.6538L49.6539 14.6538Z"
          fill={`url(#${gradientId})`}
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="9.41753e-06"
          y="-0.000112653"
          width="99.3078"
          height="69.3078"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="1.48019"
            operator="dilate"
            in="SourceAlpha"
            result="effect1_dropShadow_10778_10081"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="6.58686" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_10778_10081"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_10778_10081"
            result="shape"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2.95127" />
          <feGaussianBlur stdDeviation="1.11015" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.75 0"
          />
          <feBlend
            mode="normal"
            in2="shape"
            result="effect2_innerShadow_10778_10081"
          />
        </filter>
        {gradients}
      </defs>
    </svg>
  );
};
